// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { SupportSnackbar } from '@/components/support-snackbar';
import { MergeLocationDialog } from '@/location/components/merge-location-dialog';
import { useLocation } from '@/location/hooks/use-location';
import { CircularProgress, Dialog, DialogContent, Stack } from '@mui/material';
import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

interface Props {
  zoneKey: string;
  isOpen: boolean;
  onClose: () => void;
}

// The zones table holds sparse zones, but the merge dialog compares whole location records,
// so the zone is loaded through the shared hook before the dialog opens
function MergeZoneDialogContent(props: Props) {
  const { location } = useLocation(props.zoneKey);

  return (
    <MergeLocationDialog
      location={location}
      isOpen={props.isOpen}
      onClose={props.onClose}
    />
  );
}

// The dialog frame shows immediately so the merge action never looks inert while the zone loads
function LoadingDialog(props: { isOpen: boolean; onClose: () => void }) {
  return (
    <Dialog open={props.isOpen} onClose={props.onClose} maxWidth="lg" fullWidth>
      <DialogContent>
        <Stack alignItems="center" padding={4}>
          <CircularProgress />
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

export function MergeZoneDialog(props: Props) {
  return (
    <ErrorBoundary
      fallbackRender={() => (
        <SupportSnackbar
          isOpen
          message="An error occurred while loading the zone."
          onClose={props.onClose}
        />
      )}
    >
      <Suspense
        fallback={
          <LoadingDialog isOpen={props.isOpen} onClose={props.onClose} />
        }
      >
        <MergeZoneDialogContent
          zoneKey={props.zoneKey}
          isOpen={props.isOpen}
          onClose={props.onClose}
        />
      </Suspense>
    </ErrorBoundary>
  );
}
